import React, { useState } from "react";
import { Box, Typography, Button, Collapse } from "@mui/material";
import { Icon } from "@iconify/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

function ErrorBoundaryFallback({ error, resetErrorBoundary }) {
  const [showDetails, setShowDetails] = useState(false);

  const handleRefresh = () => {
    window.location.reload();
  };

  const isDev = Boolean(import.meta.env?.DEV);
  const hasErrorMessage = isDev && error?.message;

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          maxWidth: 440,
          width: "100%",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "0 24px 48px rgba(0,0,0,0.4)"
              : "0 24px 48px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)",
          overflow: "hidden",
          border: (t) =>
            t.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ pt: 4, px: 3, pb: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "error.main",
              color: "error.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Icon icon="mdi:alert-circle-outline" width={36} height={36} />
          </Box>
          <Typography variant="h6" component="h1" fontWeight={600} gutterBottom>
            Something went wrong
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.5, mb: 3 }}
          >
            An unexpected error occurred. Try again or refresh the page—if it
            keeps happening, please contact support.
          </Typography>
        </Box>

        {hasErrorMessage && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowDetails((v) => !v)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: "text.secondary", textTransform: "none" }}
            >
              {showDetails ? "Hide" : "Show"} technical details
            </Button>
            <Collapse in={showDetails}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  display: "block",
                  p: 2,
                  mb: 2,
                  overflow: "auto",
                  maxHeight: 140,
                  bgcolor: "action.hover",
                  borderRadius: 1.5,
                  textAlign: "left",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                }}
              >
                {error.message}
              </Typography>
            </Collapse>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
            p: 3,
            pt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={resetErrorBoundary}
            sx={{ minWidth: 120, textTransform: "none", fontWeight: 600 }}
          >
            Try again
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            sx={{ minWidth: 120, textTransform: "none", fontWeight: 600 }}
          >
            Refresh page
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ErrorBoundaryFallback;
